
import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { logger } from '../services/logger';
import type { SystemStats, LogEntry } from '../types';
import { 
  CloudIcon, 
  ArrowPathIcon, 
  CheckBadgeIcon, 
  ServerIcon, 
  BoltIcon, 
  CommandLineIcon,
  ListBulletIcon,
  SparklesIcon,
  XMarkIcon
} from './Icons';
import { useNavigate } from 'react-router-dom';
import ApiDocs from './ApiDocs';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'system' | 'logs' | 'observability' | 'api'>('system');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      const data = await api.getSystemStats();
      setStats(data);
      setIsLoading(false);
    };
    
    const refreshLogs = () => {
      setLogs(logger.getLogs());
    };

    fetchStats();
    refreshLogs();

    const interval = setInterval(fetchStats, 5000);
    window.addEventListener('agri_new_log', refreshLogs);

    return () => {
      clearInterval(interval);
      window.removeEventListener('agri_new_log', refreshLogs);
    };
  }, []);

  if (isLoading || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, sub, icon: Icon, color }: any) => (
    <div className="bg-card-bg p-6 rounded-3xl shadow-sm border border-card-border flex flex-col justify-between hover:shadow-lg transition-all duration-300">
      <div className="flex justify-between items-start">
        <div className={`p-3 rounded-2xl ${color} bg-opacity-10`}>
          <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
        </div>
        <span className="text-[10px] font-black uppercase text-app-text-secondary tracking-widest">{title}</span>
      </div>
      <div className="mt-4">
        <h4 className="text-3xl font-black text-brand-brown">{value}</h4>
        <p className="text-xs text-app-text-secondary font-bold mt-1 uppercase tracking-tighter">{sub}</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto pb-12 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-black text-brand-green flex items-center gap-3">
            <ServerIcon className="w-10 h-10" />
            Infrastructure Monitor
          </h1>
          <p className="text-app-text-secondary font-bold uppercase text-xs tracking-[0.2em] mt-1">
            AgriConnect Enterprise • Monitoring & Observability
          </p>
        </div>
        <div className="flex gap-2">
            <button 
                onClick={() => navigate('/')}
                className="bg-card-bg border-2 border-brand-green text-brand-green px-6 py-2.5 rounded-full font-black text-sm flex items-center gap-2 hover:bg-brand-green hover:text-white transition-all shadow-md"
            >
                <ListBulletIcon className="w-5 h-5" />
                Marketplace
            </button>
        </div>
      </div>

      <div className="flex gap-4 mb-8 border-b border-card-border overflow-x-auto pb-1 no-scrollbar">
          {[
              { id: 'system', label: 'System Health', icon: ServerIcon },
              { id: 'logs', label: 'Live Logs', icon: CommandLineIcon },
              { id: 'observability', label: 'Observability', icon: SparklesIcon },
              { id: 'api', label: 'API Specs', icon: CommandLineIcon }
          ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-t-xl text-sm font-black uppercase tracking-widest transition-all shrink-0 ${
                    activeTab === tab.id 
                    ? 'bg-brand-green text-white border-b-2 border-brand-yellow' 
                    : 'bg-card-bg text-app-text-secondary hover:text-brand-green'
                }`}
              >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
              </button>
          ))}
      </div>

      {activeTab === 'system' && (
        <div className="space-y-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Database" 
                    value={stats.dbStatus === 'CONNECTED' ? 'Active' : 'Offline'} 
                    sub={stats.dbType}
                    icon={CheckBadgeIcon}
                    color="bg-green-500"
                />
                <StatCard 
                    title="Cache Efficiency" 
                    value={`${stats.cacheHitRate.toFixed(1)}%`} 
                    sub="Redis 7.2.4 L1/L2"
                    icon={BoltIcon}
                    color="bg-brand-yellow"
                />
                <StatCard 
                    title="Sync Queue" 
                    value={stats.syncQueueSize} 
                    sub="Pending Transactions"
                    icon={ArrowPathIcon}
                    color="bg-blue-500"
                />
                <StatCard 
                    title="Avg. Latency" 
                    value={`${stats.latencyMs}ms`} 
                    sub="Cloud-Edge Roundtrip"
                    icon={CloudIcon}
                    color="bg-purple-500"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <div className="bg-[#1e1e1e] rounded-3xl p-6 shadow-2xl overflow-hidden border border-white/10">
                        <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-4">
                            <div className="flex items-center gap-3">
                                <CommandLineIcon className="w-6 h-6 text-green-400" />
                                <h3 className="text-white font-black uppercase text-xs tracking-widest">Live Sync Streams</h3>
                            </div>
                            <span className="text-[10px] text-green-400 font-mono animate-pulse uppercase">Recording...</span>
                        </div>
                        <div className="font-mono text-[11px] space-y-1.5 h-64 overflow-y-auto custom-scrollbar text-green-400/80">
                            {logs.slice(0, 15).map(log => (
                                <p key={log.id} className={log.level === 'error' ? 'text-red-400' : log.level === 'warn' ? 'text-yellow-400' : ''}>
                                    <span className="text-white/70">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
                                </p>
                            ))}
                            <div className="animate-pulse">_</div>
                        </div>
                    </div>
                </div>

                <div className="bg-brand-green rounded-3xl p-8 text-white shadow-xl relative overflow-hidden flex flex-col justify-center">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16" />
                    <h3 className="text-2xl font-black mb-6">Uptime Monitor</h3>
                    <div className="space-y-6">
                        <div>
                            <p className="text-[10px] font-black uppercase text-white/80 tracking-widest mb-1">System Uptime</p>
                            <p className="text-3xl font-black">{Math.floor(stats.uptimeSeconds / 60)}m {stats.uptimeSeconds % 60}s</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase text-white/80 tracking-widest mb-1">Request Throughput</p>
                            <p className="text-3xl font-black">{stats.requestsPerMinute} RPM</p>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                            <div className="flex items-center gap-2 text-brand-yellow font-black text-xs uppercase">
                                <CheckBadgeIcon className="w-4 h-4" />
                                All Systems Operational
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      )}

      {activeTab === 'logs' && (
          <div className="bg-[#1e1e1e] rounded-3xl shadow-2xl overflow-hidden flex flex-col h-[600px] border border-white/10">
              <div className="bg-black/50 p-4 flex justify-between items-center border-b border-white/10">
                  <h3 className="text-white font-black uppercase text-xs tracking-widest flex items-center gap-2">
                      <CommandLineIcon className="w-4 h-4 text-green-400" />
                      Production Application Logs
                  </h3>
                  <button 
                    onClick={() => {logger.clearLogs(); setLogs([]);}}
                    className="text-[10px] font-black text-white/75 hover:text-white uppercase px-3 py-1 bg-white/5 rounded transition-all"
                  >
                      Clear Logs
                  </button>
              </div>
              <div className="p-6 overflow-y-auto custom-scrollbar flex-grow font-mono text-xs space-y-2">
                  {logs.length === 0 ? (
                      <p className="text-white/75 italic">No logs captured in current session.</p>
                  ) : logs.map(log => (
                      <div key={log.id} className={`flex gap-4 border-b border-white/5 pb-2 last:border-0 ${
                          log.level === 'error' ? 'text-red-400' : 
                          log.level === 'warn' ? 'text-yellow-400' : 
                          log.level === 'debug' ? 'text-blue-400' : 'text-green-400/70'
                      }`}>
                          <span className="text-white/70 shrink-0">[{new Date(log.timestamp).toISOString().split('T')[1].split('Z')[0]}]</span>
                          <span className="font-black uppercase tracking-tighter w-12 shrink-0">[{log.level}]</span>
                          <span>
                            {log.message}
                            {log.context && (
                                <pre className="mt-1 text-[10px] bg-black/40 p-2 rounded overflow-x-auto opacity-80">
                                    {JSON.stringify(log.context, null, 2)}
                                </pre>
                            )}
                          </span>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {activeTab === 'observability' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card-bg p-8 rounded-3xl shadow-sm border border-card-border">
                  <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-red-100 rounded-2xl">
                          <XMarkIcon className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                          <h3 className="text-xl font-black text-brand-brown">Error Tracking</h3>
                          <p className="text-xs text-app-text-secondary font-bold uppercase tracking-tighter">Sentry Integration Simulator</p>
                      </div>
                  </div>
                  <div className="space-y-4">
                      <div className="flex justify-between items-center p-4 bg-app-bg rounded-2xl">
                          <span className="text-sm font-bold text-app-text-secondary">Global Error Rate</span>
                          <span className={`font-black text-xl ${stats.errorRate > 5 ? 'text-red-500' : 'text-green-500'}`}>
                              {stats.errorRate.toFixed(2)}%
                          </span>
                      </div>
                      <div className="p-4 bg-brand-cream/30 rounded-2xl border border-brand-cream text-xs italic text-app-text-secondary">
                          "Phase 12 utilizes an active sampling strategy to capture stack traces from intermittent network failures in high-latency agricultural regions."
                      </div>
                  </div>
              </div>

              <div className="bg-card-bg p-8 rounded-3xl shadow-sm border border-card-border">
                  <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-brand-yellow/20 rounded-2xl">
                          <BoltIcon className="w-6 h-6 text-brand-yellow" />
                      </div>
                      <div>
                          <h3 className="text-xl font-black text-brand-brown">APM Metrics</h3>
                          <p className="text-xs text-app-text-secondary font-bold uppercase tracking-tighter">Datadog Performance Simulation</p>
                      </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-grow">
                            <div className="flex justify-between mb-1">
                                <span className="text-[10px] font-black uppercase text-app-text-secondary">P95 Latency</span>
                                <span className="text-[10px] font-black text-brand-green">{stats.latencyMs}ms</span>
                            </div>
                            <div className="w-full bg-brand-green-50 h-2 rounded-full overflow-hidden">
                                <div 
                                    className="bg-brand-green h-full transition-all duration-500" 
                                    style={{ width: `${Math.min(100, (stats.latencyMs / 500) * 100)}%` }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex-grow">
                            <div className="flex justify-between mb-1">
                                <span className="text-[10px] font-black uppercase text-app-text-secondary">Success Rate</span>
                                <span className="text-[10px] font-black text-brand-green">{100 - stats.errorRate}%</span>
                            </div>
                            <div className="w-full bg-brand-green-50 h-2 rounded-full overflow-hidden">
                                <div 
                                    className="bg-brand-light-green h-full transition-all duration-500" 
                                    style={{ width: `${100 - stats.errorRate}%` }}
                                />
                            </div>
                        </div>
                    </div>
                  </div>
              </div>
          </div>
      )}

      {activeTab === 'api' && <ApiDocs />}
    </div>
  );
};

export default AdminDashboard;
